import { useEffect, useMemo, useState } from "react";
import { hubIconMap } from "./sterlingIconMap";
import { ibmLogo } from "../../assets";

const STAGE = { width: 500, height: 520 };
const CENTER = { x: 50, y: 50 };
const ASPECT = STAGE.width / STAGE.height;
const CENTER_LINE_INSET = 36;
const NODE_LINE_INSET = 30;

const LAYOUTS = {
  desktop: {
    orbitShort: 24,
    orbitLong: 36,
    customerRadius: 36,
    spreadX: 1,
    iconSize: 24,
  },
  mobile: {
    orbitShort: 27,
    orbitLong: 39,
    customerRadius: 27,
    spreadX: 1.1,
    iconSize: 20,
  },
  small: {
    orbitShort: 25,
    orbitLong: 37,
    customerRadius: 25,
    spreadX: 1.14,
    iconSize: 18,
  },
};

const nodeDefs = [
  { label: "eCommerce", lines: ["eCommerce"] },
  { label: "POS/Stores", lines: ["POS/Stores"] },
  { label: "WMS", lines: ["WMS"] },
  { label: "ERP", lines: ["ERP"] },
  { label: "Distribution Centers", lines: ["Distribution", "Centers"] },
  { label: "Carriers", lines: ["Carriers"] },
  { label: "Inventory", lines: ["Inventory"] },
  { label: "Marketplace", lines: ["Marketplace"] },
  { label: "Customers", lines: ["Customers"], useCustomerRadius: true },
];

const toSvgCoord = (percent, axis) =>
  (percent / 100) * (axis === "x" ? STAGE.width : STAGE.height);

const trimLine = (x1, y1, x2, y2, insetStart, insetEnd) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const trimmedLength = length - insetStart - insetEnd;

  if (trimmedLength <= 0) {
    return { x1, y1, x2, y2 };
  }

  return {
    x1: x1 + ux * insetStart,
    y1: y1 + uy * insetStart,
    x2: x2 - ux * insetEnd,
    y2: y2 - uy * insetEnd,
  };
};

const useHubLayout = () => {
  const [layoutKey, setLayoutKey] = useState("desktop");

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setLayoutKey("small");
      } else if (width <= 768) {
        setLayoutKey("mobile");
      } else {
        setLayoutKey("desktop");
      }
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  return { layoutKey, layout: LAYOUTS[layoutKey] };
};

const buildPositions = (layout) =>
  nodeDefs.map((node, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 9;
    const radius = node.useCustomerRadius
      ? layout.customerRadius
      : i % 2 === 0
        ? layout.orbitShort
        : layout.orbitLong;

    return {
      top: CENTER.y + radius * ASPECT * Math.sin(angle),
      left: CENTER.x + radius * layout.spreadX * Math.cos(angle),
      nodeLineInset: node.nodeLineInset ?? NODE_LINE_INSET,
    };
  });

const HubDiagram = () => {
  const { layoutKey, layout } = useHubLayout();
  const positions = useMemo(() => buildPositions(layout), [layout]);
  const centerSvg = {
    x: toSvgCoord(CENTER.x, "x"),
    y: toSvgCoord(CENTER.y, "y"),
  };

  return (
    <div className={`sterling-hub-wrap sterling-hub-wrap--${layoutKey}`}>
      <div className="sterling-hub-stage">
        <svg
          className="sterling-hub-lines"
          viewBox={`0 0 ${STAGE.width} ${STAGE.height}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          {positions.map((pos, i) => {
            const endX = toSvgCoord(pos.left, "x");
            const endY = toSvgCoord(pos.top, "y");
            const line = trimLine(
              centerSvg.x,
              centerSvg.y,
              endX,
              endY,
              CENTER_LINE_INSET,
              pos.nodeLineInset
            );
            return (
              <line
                key={i}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="rgba(99, 90, 217, 0.28)"
                strokeWidth="1"
                strokeDasharray="4 5"
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        <div className="sterling-hub-center">
          <img src={ibmLogo} alt="IBM" className="sterling-hub-center-logo" />
          <span>Sterling OMS</span>
        </div>

        {nodeDefs.map((node, i) => {
          const Icon = hubIconMap[i];
          const pos = positions[i];
          return (
            <div
              key={node.label}
              className="sterling-hub-node"
              style={{
                top: `${pos.top}%`,
                left: `${pos.left}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                className={`sterling-hub-node-circle ${node.lines.length > 1 ? "is-multiline" : ""}`}
              >
                <Icon size={layout.iconSize} strokeWidth={1.6} />
                <div className="sterling-hub-node-text">
                  {node.lines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HubDiagram;
